import { useState, useEffect } from "react";
import { Container, Button, Row, Col, Form, Spinner, OverlayTrigger, Popover } from "react-bootstrap";

import "./index.scss";
import { useBetween } from "use-between";
import useShopify from "../../../useShopify";
import { useParams } from "react-router-dom";
import { countryList } from "./country";
import EncoreLogo from "../../../assets/images/encore-logo.png";
import WizTextfield from "../../../components/wiz/wiz-textfield";
import WizButton from "../../../components/wiz/wiz-button";
import WizSelect from "../../../components/wiz/wiz-select";

const Address = ({ prev, next, form, setForm, selectionState }) => {
  const { company, campaign: campaignName } = useParams();
  const { campaign, product, createOrder, note, image } =
    useBetween(useShopify);

  const isEncore = campaign.companyId === "65cd150fdc2cf13513530120";
  const isLiveNation = campaign.companyId === "65d52364dc2cf135135301fd";
  const isImpiricus = campaign.companyId === "66639212d7476d285f2b1cfe";
  const isRegent =
    campaign.companyId === process.env.REACT_APP_REGENT_COMPANY_ID;

  const isModernHealth = campaign.companyId === "67294a3bdca127a271571be4" || campaign.companyId === "68fa77f6cee86b5fec95da46";
  const isDeclinedGift = isModernHealth && product?.handle === "decline-gift";

  const isConcentrix = campaign.companyId === "6792c702dca127a271574e4d";
  const isWiz = campaign.companyId === "67981cfbdca127a271575000";
  const isEdwardsLS = campaign.companyId === "67f6b6badca127a27157640b";
  const isGusto = campaign.companyId === "68140062cee86b5fec95476e";
  const isCyera = campaign.companyId === "6840f57ecee86b5fec9562c6";
  const isKlingAI = campaign.companyId === "68643ac2cee86b5fec9574f1";
  const isNewGusto = campaign.companyId === "68c3332bcee86b5fec9588ab";
  const isNewCyera = campaign.companyId === "6840f57ecee86b5fec9562c6";
  const isCapitalOne = campaign.companyId === "691f7871cee86b5fec95f6ab";
  const isOptedOut = false; // No longer needed since marketingCode field is removed for isNewGusto
  const isCyeraSecretSanta2 = campaign.name === "CYERA SECRET SANTA GIFTS";
  const shouldHideFields = isCapitalOne && product?.handle === "donate-to-world-central-kitchen";

  const {
    skuOptions,
    sizeOptions,
    genderOptions,
    selections,
    currentGenderIndex,
    currentOptionIndex,
    selectedSizes,
    isSelectionOpen,
  } = selectionState;

  // World Cruise 2027 uses a narrower centered column and 3 picks per guest.
  const isRegentWorldCruise2027 = isRegent && (campaign?.name || '').toLowerCase().replace(/[-\s]/g, '').includes('worldcruise2027');
  // Size slots per guest, matching calculateSizeIndex in the gifts step.
  const sizeStride = Math.max(2, ...(skuOptions || []).map(options => options?.length || 0));

  const [pending, setPending] = useState(false);
  const [isStateNotApplicable, setIsStateNotApplicable] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [introductionCharCount, setIntroductionCharCount] = useState(0);

  const handleStateNotApplicableChange = (e) => {
    const isChecked = e.target.checked;
    setIsStateNotApplicable(isChecked);
    
    if (isChecked) {
      setForm({ ...form, state: "N/A" });
    } else {
      setForm({ ...form, state: "" });
    }
  };

  const handleStateInputChange = (e) => {
    const value = e.target.value;
    setForm({ ...form, state: value });
    
    // If user starts typing, uncheck the "Not applicable" checkbox
    if (value && value !== "N/A") {
      setIsStateNotApplicable(false);
    }
  };

  const validateCyeraEmail = (email) => {
    if (isNewCyera && email && !email.toLowerCase().endsWith("@cyera.io")) {
      return "Only @cyera.io email addresses are allowed";
    }
    return "";
  };

  const handleEmailChange = (e) => {
    const email = e.target.value;
    setForm({ ...form, email });
    
    // Clear previous error when user starts typing
    if (emailError) {
      setEmailError("");
    }
    
    // Validate email for Cyera
    if (isNewCyera) {
      const error = validateCyeraEmail(email);
      setEmailError(error);
    }
  };

  const handleIntroductionChange = (e) => {
    const value = e.target.value;
    if (value.length <= 250) {
      setForm({ ...form, marketingCode: value });
      setIntroductionCharCount(value.length);
    }
  };

  // Initialize character count for introduction field
  useEffect(() => {
    if (isNewCyera && form.marketingCode) {
      setIntroductionCharCount(form.marketingCode.length);
    }
  }, [isNewCyera, form.marketingCode]);



  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate email for Cyera before submission
    if (isNewCyera && form.email) {
      const emailValidationError = validateCyeraEmail(form.email);
      if (emailValidationError) {
        setEmailError(emailValidationError);
        return;
      }
    }
    
    setPending(true);

    const formData = new FormData();
    formData.append("campaignId", campaign._id);
    formData.append("firstName", shouldHideFields ? "N/A" : form.firstName);
    formData.append("lastName", shouldHideFields ? "N/A" : form.lastName);
    formData.append("companyName", shouldHideFields ? "N/A" : form.companyName);
    formData.append("originalCompany", company);
    formData.append("streetAddress", shouldHideFields ? "N/A" : form.streetAddress);
    formData.append("suite", shouldHideFields ? "" : form.suite);
    formData.append("city", shouldHideFields ? "N/A" : form.city);
    formData.append("state", shouldHideFields ? "N/A" : form.state);
    formData.append("postalCode", shouldHideFields ? "N/A" : form.postalCode);
    formData.append("country", shouldHideFields ? "N/A" : form.country);
    formData.append("phoneNumber", shouldHideFields ? "" : form.phoneNumber);
    formData.append("email", form.email);
    formData.append("size", form.size);
    formData.append("boxName", product.handle);
    formData.append("note", note);
    formData.append("campaign", campaignName);
    formData.append("image", image);
    formData.append("venueName", form.venueName);
    formData.append("marketingCode", isNewGusto ? "" : form.marketingCode);
    formData.append("affiliatedBrands", form.affiliatedBrands);
    if (isRegent) {
      formData.append("bookingNumber", form.bookingNumber);
    }

    if (isRegent) {
      const productHandles = selections
        .flat()
        .map((product) => product.handle)
        .join(", ");
      const selectedSizesList = Object.values(selectedSizes).join(", ");
      formData.set("boxName", productHandles);
      formData.set("size", selectedSizesList);
    } else {
      formData.set("boxName", product.handle);
      formData.set("size", form.size);
    }

    if (isLiveNation) {
      setPending(false);
      next();
    } else {
      // next();
      // return;
      createOrder(formData)
        .then(() => {
          setPending(false);
          next();
        })
        .catch((e) => {
          if (
            e.message.includes("Order with this booking number already exists")
          ) {
            alert("Order with this booking number already exists");
          } else {
            setPending(false);
            alert("Please try again");
          }
        });
    }
  };

  const countries = countryList;
  const sizes = (campaign.sizes || "")
    .split(",")
    .map((size) => size.trim())
    .filter((size) => size.length);

  const renderSelectionSummary = () => {
    if (!isRegent) return null;

    return (
      <div className="selection-summary mb-4">
        <h3>Your Selection</h3>
        {genderOptions.map((gender, genderIndex) => (
          <div key={genderIndex}>
            <p>
              <strong>{gender}:</strong>
            </p>
            <ul>
              {selections[genderIndex].map((selectionProducts, selIndex) => {
                const size = selectedSizes[genderIndex * sizeStride + selIndex];
                const productName =
                  selectionProducts?.title || `Product ${selIndex + 1}`;
                return (
                  <li key={selIndex}>
                    {productName} - {size}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    );
  };

  // Helper to check if a specific field is empty
  const isFieldEmpty = (fieldName) => !form[fieldName];
  const cyeraSecretSantaFieldNames = ["email", "firstName", "lastName"];
  const shouldShowMarketingCodeField =
    !isCyeraSecretSanta2 &&
    ((!isDeclinedGift && isLiveNation && !isEdwardsLS) ||
      isCyera ||
      isNewCyera);

  // List of required fields and their labels
  const requiredFields = [
    { name: 'email', label: isWiz ? "Please enter your email here" : isGusto ? "Gusto Email" : 'Email', type: 'email', placeholder: 'john@example.com', required: !isLiveNation, show: !isEdwardsLS },
    { name: 'firstName', label: isWiz ? "Recipient's First Name" : 'First Name', type: 'text', required: true },
    { name: 'lastName', label: isWiz ? "Recipient's Last Name" : 'Last Name', type: 'text', required: true },
    { name: 'phoneNumber', label: isWiz ? "Recipient's Phone Number" : 'Phone Number', type: 'number', required: !isLiveNation, show: !isEdwardsLS },
    { name: 'companyName', label: isWiz ? "Recipient's Company Name" : `${isLiveNation ? 'Client ' : ''}Company Name`, type: 'text', placeholder: isImpiricus ? "" : 'Not required for Residential Addresses', required: isImpiricus, show: !isDeclinedGift && !isEdwardsLS && !isNewGusto },
    { name: 'affiliatedBrands', label: 'What brands are you affiliated with?', type: 'text', required: false, show: !isDeclinedGift && isImpiricus && !isEdwardsLS },
    { name: 'streetAddress', label: isWiz ? "Recipient's Street Address" : 'Street Address', type: 'text', required: true, show: !isDeclinedGift && !isEdwardsLS },
    { name: 'suite', label: isWiz ? "Recipient's Apt/Suite #" : 'Apt/Suite #', type: 'text', required: false, show: !isDeclinedGift && !isEdwardsLS },
    { name: 'postalCode', label: isWiz ? "Recipient's Zip/Postal Code" : 'Zip/Postal Code', type: 'text', required: true, show: !isDeclinedGift && !isEdwardsLS },
    { name: 'city', label: isWiz ? "Recipient's City" : 'City', type: 'text', required: true, show: !isDeclinedGift && !isEdwardsLS },
    { name: 'country', label: isWiz ? "Recipient's Country" : 'Country', type: 'select', required: true, show: !isDeclinedGift && !isEdwardsLS },
    { name: 'state', label: isWiz ? "Recipient's State" : 'State', type: 'text', placeholder: 'Abbreviation (Example: CA)', required: true, show: !isDeclinedGift && !isEdwardsLS },
    { name: 'venueName', label: 'Venue Name', type: 'text', required: true, show: !isDeclinedGift && isLiveNation && !isEdwardsLS },
    { name: 'marketingCode', label: isNewCyera ? "Tell your secret santa a little about yourself" : isCyera ? "How many children do you have?" : isImpiricus ? "Birthday" : isNewGusto ? "If you choose not to opt in, no worries, your $50 gift value will be donated to <strong>Toys for Tots' Literacy Program</strong>. Helping provide books and educational resources to our next generation of Small Business Owners. A win-win! 💝" : 'Caterease Bill to Event Code OR Marketing code', type: isNewCyera ? 'textarea' : isCyera ? 'number' : isImpiricus ? 'date' : isNewGusto ? 'text' : 'text', required: isNewCyera ? false : isCyera ? true : isImpiricus ? false : isNewGusto ? false : true, show: shouldShowMarketingCodeField },
    // add size for klingAI only
    { name: 'size', label: 'T-Shirt Size', type: 'select', required: true, show: isKlingAI, hasInfo: isKlingAI }
  ];

  const renderFormFields = () => {
    if (isRegent) {
      return null;
    }

    // Create popover content for size info
    const sizeInfoPopover = (
      <Popover id="size-info-popover">
        {/* <Popover.Header as="h6">Sizing Information</Popover.Header> */}
        <Popover.Body>
          Sizing is for a T-shirt in Asian unisex size. You may want to size up and select a larger size than your normal size, depending on your preferences. Size selection is first-come-first-serve and no accommodation can be made if there is no availability in your desired size.
        </Popover.Body>
      </Popover>
    );

    // For non-Regent customers, show all fields
    return (
      <Row>
        {requiredFields
          .filter(field => {
            if (isCyeraSecretSanta2) {
              return cyeraSecretSantaFieldNames.includes(field.name);
            }
            if (shouldHideFields) {
              return field.name === 'email' && field.show !== false;
            }
            return field.show !== false;
          })
          .map(field => {
            // Special handling for state field
            if (field.name === 'state') {
              return (
                <Col md={6} key={field.name}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      {field.label}{field.required && <em>*</em>}
                    </Form.Label>
                    {isWiz ? (
                      <div>
                        <WizTextfield
                          type={field.type}
                          placeholder={field.placeholder}
                          value={form[field.name]}
                          onChange={handleStateInputChange}
                          required={field.required && !isStateNotApplicable}
                          disabled={isStateNotApplicable}
                        />
                        <div className="mt-2">
                          <Form.Check
                            type="checkbox"
                            id="state-not-applicable"
                            label="Not applicable"
                            checked={isStateNotApplicable}
                            onChange={handleStateNotApplicableChange}
                            style={{ color: "#1e50e7" }}
                          />
                        </div>
                      </div>
                    ) : (
                      <div>
                        <Form.Control
                          type={field.type}
                          required={field.required && !isStateNotApplicable}
                          placeholder={field.placeholder}
                          value={form[field.name]}
                          onChange={handleStateInputChange}
                          disabled={isStateNotApplicable}
                        />
                        <div className="mt-2">
                          <Form.Check
                            type="checkbox"
                            id="state-not-applicable"
                            label="Not applicable"
                            checked={isStateNotApplicable}
                            onChange={handleStateNotApplicableChange}
                          />
                        </div>
                      </div>
                    )}
                  </Form.Group>
                </Col>
              );
            }

            // Regular field rendering
            return (
              <Col md={6} key={field.name}>
                <Form.Group className="mb-3">
                  {!(isNewGusto && field.name === 'marketingCode') && (
                    <Form.Label className="d-flex align-items-center gap-2">
                      <span>{field.label}{field.required && <em>*</em>}</span>
                      {field.hasInfo && (
                        <OverlayTrigger
                          trigger="click"
                          placement="top"
                          overlay={sizeInfoPopover}
                          rootClose
                        >
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center"
                            style={{ 
                              width: '20px', 
                              height: '20px', 
                              fontSize: '12px',
                              border: '1px solid #6c757d',
                              backgroundColor: '#f8f9fa',
                              cursor: 'pointer',
                              userSelect: 'none'
                            }}
                            onClick={(e) => e.preventDefault()}
                          >
                            ?
                          </div>
                        </OverlayTrigger>
                      )}
                    </Form.Label>
                  )}
                  {field.type === 'select' ? (
                    isWiz ? (
                      <WizSelect
                        options={countries}
                        value={form[field.name]}
                        onChange={(e) => setForm({ ...form, [field.name]: e.target.value })}
                        required={field.required}
                      />
                    ) : (
                      <Form.Select
                        required={field.required}
                        value={form[field.name]}
                        onChange={(e) => setForm({ ...form, [field.name]: e.target.value })}
                      >
                        { field.name === 'size' && isKlingAI ? (
                          <>
                          <option value="">Select Size</option>
                          <option value="M">M</option>
                          <option value="L">L</option>
                          <option value="XL">XL</option>
                          <option value="XXL">XXL</option>
                          </>
                        ) : (
                          countries.map((value, index) => (
                            <option key={index}>{value}</option>
                          ))
                        )}
                      </Form.Select>
                    )
                  ) : field.type === 'checkbox' ? (
                    <Form.Check
                      type="checkbox"
                      id={field.name}
                      label={field.label}
                      checked={form[field.name] || false}
                      onChange={(e) => setForm({ ...form, [field.name]: e.target.checked })}
                      required={field.required}
                    />
                  ) : isNewGusto && field.name === 'marketingCode' ? (
                    <div 
                      style={{ 
                        padding: '10px', 
                        borderRadius: '5px', 
                        color: '#6c757d',
                        fontSize: '14px',
                        textAlign: 'left',
                        lineHeight: '1.4'
                      }}
                      dangerouslySetInnerHTML={{ __html: field.label }}
                    />
                  ) : field.type === 'textarea' ? (
                    <Form.Control
                      as="textarea"
                      rows={4}
                      required={field.required}
                      placeholder={field.placeholder}
                      value={form[field.name]}
                      onChange={field.name === 'marketingCode' && isNewCyera ? handleIntroductionChange : (e) => setForm({ ...form, [field.name]: e.target.value })}
                      maxLength={250}
                    />
                  ) : isWiz ? (
                    <WizTextfield
                      type={field.type}
                      placeholder={field.placeholder}
                      value={form[field.name]}
                      onChange={field.name === 'email' ? handleEmailChange : (e) => setForm({ ...form, [field.name]: e.target.value })}
                      required={field.required}
                    />
                  ) : (
                    <Form.Control
                      type={field.type}
                      required={field.required}
                      placeholder={field.placeholder}
                      value={form[field.name]}
                      onChange={field.name === 'email' ? handleEmailChange : (e) => setForm({ ...form, [field.name]: e.target.value })}
                    />
                  )}
                  {field.name === 'email' && emailError && (
                    <div className="text-danger mt-1" style={{ fontSize: '14px' }}>
                      {emailError}
                    </div>
                  )}
                  {field.name === 'marketingCode' && isNewCyera && (
                    <div className="text-muted mt-1" style={{ fontSize: '12px', textAlign: 'right' }}>
                      {introductionCharCount}/250 characters
                    </div>
                  )}
                </Form.Group>
              </Col>
            );
          })}
      </Row>
    );
  };

  return (
    <div style={ isWiz ? { color: "#1e50e7" } : null } className={`address-container ${isEncore ? "encore-font" : "null"} ${isRegentWorldCruise2027 ? "address-container--compact" : ""}`}>
      <Container>
        {isEncore && (
          <div className="encore-logo">
            <img src={EncoreLogo} alt="Encore Logo" />
          </div>
        )}
        {isRegent ? (
          <h2>SUBMIT YOUR SELECTIONS</h2>
        ) : isConcentrix ? (
          <h2>Kindly share your address so that we may send your gift</h2>
        ) : isEdwardsLS ? (
          <h2>Please confirm your admin's first and last name</h2>
        ) :  isWiz ? (
          <h2>WE'D LOVE YOUR RECIPIENT'S MAILING ADDRESS</h2>
        ) : isCyeraSecretSanta2 ? (
          <h2>LET US KNOW WHO YOU GOT:</h2>
        ) : (
          <h2>{isLiveNation ? "CLIENT" : "WE'D LOVE YOUR"} MAILING ADDRESS</h2>
        )}
        <p>
          {campaign.checkoutText
            ? campaign.checkoutText
            : "Please fill out your preferred shipping address below!"}
        </p>
        {renderSelectionSummary()}
        <Form
          className="w-100"
          onSubmit={(event) => {
            handleSubmit(event);
          }}
        >
          {renderFormFields()}

          <p className="privacy mb-4">
            { isConcentrix ? 'Your privacy is important to us. The address and phone number provided here will only be used for the purpose of sending your gift.' : "Your privacy is important to us. We never sell your information and it will only be used for the purpose of sending your gift." }
            {isEncore
              ? "Once you submit your address and we ship your gift, your information is permanently deleted from our database"
              : null}
          </p>
          <Row>
            <Col md={6} className="md-order-12">
              {isLiveNation ? (
                <Button type="submit" variant="secondary" className="mb-4">
                  Review
                </Button>
              ) : isWiz ? (
                <WizButton type="submit" className="mb-4">
                  {!pending ? <>Submit</> : <Spinner animation="border" />}
                </WizButton>
              ) : isNewGusto ? (
                <Button style={{backgroundColor: campaign.buttonColor || null, color: campaign.buttonTextColor || null, border: (campaign.buttonStrokeColor && `2px solid ${campaign.buttonStrokeColor}`) || null}} type="submit" variant="secondary" className="mb-4">
                  {!pending ? <>Sign me up!</> : <Spinner animation="border" />}
                </Button>
              ) : (
                <Button type="submit" variant="secondary" className="mb-4">
                  {!pending ? <>Submit</> : <Spinner animation="border" />}
                </Button>
              )}
            </Col>
            <Col md={6}>
              {isWiz ? (
                <WizButton
                  variant="secondary"
                  onClick={() => {
                    prev();
                  }}
                >
                  {isRegent ? "Back" : "Cancel"}
                </WizButton>
              ) : ( 
                <Button
                onClick={() => {
                  prev();
                }}
                variant="primary"
                className="mb-4"
                >
                  {isRegent ? "Back" : "Cancel"}
                </Button>
              )}
            </Col>
          </Row>
        </Form>
      </Container>
    </div>
  );
};

export default Address;
